"use client";

import { createClasses, Tab, Tabs } from "@bloomberg/media-ui-core";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ScreenContext } from "@foundation/screen";
import { useSessionStorage } from "@foundation/local-storage";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import ModeAwareTickerChart from "../ModeAwareTickerChart";
import TickerChartControls from "../TickerChartControls";
import ComparisonTickerSearch from "../ComparisonTickerSearch";
import ComparisonTickerPills from "../ComparisonTickerPills";
import LightboxContainer from "../LightboxContainer";
import styles from "./styles.module.css";
import {
  isPricePoint,
  type ChartAxisFormatters,
  type ChartType,
  type DataInterval,
  type HoverEvent,
  type TickerChartDateRange,
} from "~types/ticker-chart";
import { getDefaultInterval } from "~utils/ticker-chart";
import {
  TICKER_CHART_DATE_RANGE_TABS,
  TICKER_CHART_DATE_RANGE_TABS_WITH_RT,
} from "~constants/ticker-chart";
import { usePageData } from "~context/PageContext";

type DateRangeTickerChartProps = {
  readonly ticker: string;
  readonly chartIdentifier?: string;
  readonly enableRealTime?: boolean;
  readonly enableVolume?: boolean;
  readonly enableZoomPan?: boolean;
  readonly showBaseline?: boolean;
  readonly showMultiColorLine?: boolean;
  readonly showComparison?: boolean;
  readonly showControls?: boolean;
  readonly className?: string;
};

function DateRangeTickerChartInner(props: DateRangeTickerChartProps) {
  const {
    ticker,
    chartIdentifier = "dateRangeTickerChart",
    className,
    enableRealTime = false,
    enableVolume = false,
    enableZoomPan = false,
    showBaseline = false,
    showMultiColorLine = false,
    showComparison = false,
    showControls = false,
  } = props;

  const { countryCode, searchClientURL } = usePageData();
  const [selectedTab, setSelectedTab] = useSessionStorage(
    chartIdentifier,
    "1M"
  );

  const dateRangeTabs = useMemo(
    () =>
      enableRealTime
        ? TICKER_CHART_DATE_RANGE_TABS_WITH_RT
        : TICKER_CHART_DATE_RANGE_TABS,
    [enableRealTime]
  );
  const [chartType, setChartType] = useState<ChartType>("line");
  const [selectedInterval, setSelectedInterval] = useState<DataInterval>(() =>
    getDefaultInterval(selectedTab)
  );

  useEffect(() => {
    setSelectedInterval(getDefaultInterval(selectedTab));
  }, [selectedTab]);

  const handleChartTypeChange = useCallback((newChartType: ChartType) => {
    setChartType(newChartType);
  }, []);

  const handleIntervalChange = useCallback((newInterval: DataInterval) => {
    setSelectedInterval(newInterval);
  }, []);

  const [comparisonTickers, setComparisonTickers] = useState<string[]>([]);

  const handleAddComparisonTicker = useCallback((tickerId: string) => {
    setComparisonTickers((prev) => [...prev, tickerId]);
  }, []);

  const handleRemoveComparisonTicker = useCallback((tickerId: string) => {
    setComparisonTickers((prev) => prev.filter((t) => t !== tickerId));
  }, []);

  const [comparisonValues, setComparisonValues] = useState<
    Record<string, number | null>
  >({});

  const isComparisonMode = comparisonTickers.length > 0;

  const handleComparisonHover = useCallback((event: HoverEvent) => {
    if (event.mode !== "comparison") return;

    const values: Record<string, number | null> = {};

    for (const [seriesId, point] of Object.entries(event.seriesPoints)) {
      if (point === null) {
        values[seriesId] = null;
      } else if (isPricePoint(point)) {
        values[seriesId] = point.value;
      } else {
        values[seriesId] = point.close;
      }
    }

    setComparisonValues(values);
  }, []);

  const comparisonFormatters = useMemo<ChartAxisFormatters | undefined>(() => {
    if (!isComparisonMode) return undefined;

    return {
      yAxis: (value: number) => `${value.toFixed(2)}%`,
    };
  }, [isComparisonMode]);

  const {
    breakpoints: { largeDesktop },
  } = useContext(ScreenContext);

  const trackQuoteChartTabClick = useCallback(
    (tabTitle: string) => {
      trackClickEvent(
        `chart_${tabTitle}`,
        `chart_${tabTitle}`,
        "click",
        "engagement"
      );
      setSelectedTab(tabTitle as TickerChartDateRange);
    },
    [setSelectedTab]
  );

  return (
    <LightboxContainer className={createClasses([styles.container, className])}>
      {({
        isExpanded,
        expandedDimensions,
        onToggleExpand,
        expandButtonRef,
      }) => (
        <>
          <div className={styles.controlsRow}>
            <Tabs
              className={styles.tabs}
              size={largeDesktop ? "default" : "small"}
              variant="gliding"
              onTabSwitch={trackQuoteChartTabClick}
            >
              {dateRangeTabs.map((tab) => (
                <Tab
                  key={tab}
                  title={tab}
                  isActive={selectedTab === tab}
                  isDefault={selectedTab === tab}
                >
                  <></>
                </Tab>
              ))}
            </Tabs>

            {showComparison && (
              <ComparisonTickerSearch
                searchURL={searchClientURL}
                countryCode={countryCode}
                comparisonTickers={comparisonTickers}
                primaryTicker={ticker}
                onAdd={handleAddComparisonTicker}
              />
            )}

            {showControls && (
              <TickerChartControls
                chartType={chartType}
                interval={selectedInterval}
                dateRange={selectedTab}
                onChartTypeChange={handleChartTypeChange}
                onIntervalChange={handleIntervalChange}
                isComparisonMode={isComparisonMode}
                showExpandIcon={false}
                isExpanded={isExpanded}
                onExpandClick={onToggleExpand}
                expandButtonRef={expandButtonRef}
              />
            )}
          </div>

          {isComparisonMode && (
            <div className={styles.pillsRow}>
              <ComparisonTickerPills
                primaryTicker={ticker}
                tickers={comparisonTickers}
                onRemove={handleRemoveComparisonTicker}
                values={comparisonValues}
              />
            </div>
          )}

          <ModeAwareTickerChart
            key={`${selectedTab}-${chartType}-${selectedInterval}-${comparisonTickers.join(
              ","
            )}`}
            ticker={ticker}
            dateRange={selectedTab}
            chartType={chartType}
            comparisonTickers={comparisonTickers}
            enableRealTime={enableRealTime}
            enableVolume={enableVolume}
            enableZoomPan={enableZoomPan}
            showBaseline={showBaseline}
            showMultiColorLine={showMultiColorLine}
            height={expandedDimensions?.height}
            width={expandedDimensions?.width}
            onHover={isComparisonMode ? handleComparisonHover : undefined}
            formatters={comparisonFormatters}
          />
        </>
      )}
    </LightboxContainer>
  );
}

DateRangeTickerChartInner.displayName = "DateRangeTickerChart";

const DateRangeTickerChart = React.memo(DateRangeTickerChartInner);

export default DateRangeTickerChart;
